import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import './HomePageCMS.css';
import '../../styles/AdminShared.css';

const API = process.env.REACT_APP_API_URL || '/api';

interface ProcessStep { icon: string; title: string; title_bn: string; description: string; description_bn: string; }
interface RefundRow { method: string; method_bn: string; time: string; time_bn: string; }

interface ReturnsContent {
  hero_title: string; hero_title_bn: string;
  hero_subtitle: string; hero_subtitle_bn: string;
  policy_title: string; policy_title_bn: string;
  policy_content: string; policy_content_bn: string;
  policy_note: string; policy_note_bn: string;
  eligible_title: string; eligible_title_bn: string;
  eligible_reasons: string[];
  eligible_reasons_bn: string[];
  non_eligible_note: string; non_eligible_note_bn: string;
  process_title: string; process_title_bn: string;
  process_steps: ProcessStep[];
  refund_title: string; refund_title_bn: string;
  refund_content: string; refund_content_bn: string;
  refund_table: RefundRow[];
  exchange_title: string; exchange_title_bn: string;
  exchange_content: string; exchange_content_bn: string;
}

const EMPTY: ReturnsContent = {
  hero_title: '', hero_title_bn: '', hero_subtitle: '', hero_subtitle_bn: '',
  policy_title: '', policy_title_bn: '', policy_content: '', policy_content_bn: '',
  policy_note: '', policy_note_bn: '',
  eligible_title: '', eligible_title_bn: '',
  eligible_reasons: [], eligible_reasons_bn: [],
  non_eligible_note: '', non_eligible_note_bn: '',
  process_title: '', process_title_bn: '',
  process_steps: [],
  refund_title: '', refund_title_bn: '', refund_content: '', refund_content_bn: '',
  refund_table: [],
  exchange_title: '', exchange_title_bn: '', exchange_content: '', exchange_content_bn: '',
};

const ReturnsPageCMS: React.FC = () => {
  const [content, setContent] = useState<ReturnsContent>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchContent(); }, []);

  const fetchContent = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/admin/cms/returns/`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setContent(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setContent(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  /* Reasons */
  const addReason = () => setContent(prev => ({ ...prev, eligible_reasons: [...prev.eligible_reasons, ''], eligible_reasons_bn: [...prev.eligible_reasons_bn, ''] }));
  const removeReason = (i: number) => setContent(prev => ({
    ...prev,
    eligible_reasons: prev.eligible_reasons.filter((_, idx) => idx !== i),
    eligible_reasons_bn: prev.eligible_reasons_bn.filter((_, idx) => idx !== i),
  }));
  const updateReason = (i: number, v: string) => {
    const reasons = [...content.eligible_reasons]; reasons[i] = v;
    setContent(prev => ({ ...prev, eligible_reasons: reasons }));
  };
  const updateReasonBn = (i: number, v: string) => {
    const reasons = [...content.eligible_reasons_bn]; reasons[i] = v;
    setContent(prev => ({ ...prev, eligible_reasons_bn: reasons }));
  };

  /* Process steps */
  const addStep = () => setContent(prev => ({
    ...prev, process_steps: [...prev.process_steps, { icon: '\uD83D\uDCE6', title: '', title_bn: '', description: '', description_bn: '' }],
  }));
  const removeStep = (i: number) => setContent(prev => ({ ...prev, process_steps: prev.process_steps.filter((_, idx) => idx !== i) }));
  const updateStep = (i: number, field: keyof ProcessStep, v: string) => {
    const steps = [...content.process_steps]; steps[i] = { ...steps[i], [field]: v };
    setContent(prev => ({ ...prev, process_steps: steps }));
  };

  /* Refund table */
  const addRefundRow = () => setContent(prev => ({ ...prev, refund_table: [...prev.refund_table, { method: '', method_bn: '', time: '', time_bn: '' }] }));
  const removeRefundRow = (i: number) => setContent(prev => ({ ...prev, refund_table: prev.refund_table.filter((_, idx) => idx !== i) }));
  const updateRefundRow = (i: number, field: keyof RefundRow, v: string) => {
    const rows = [...content.refund_table]; rows[i] = { ...rows[i], [field]: v };
    setContent(prev => ({ ...prev, refund_table: rows }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault(); setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/admin/cms/returns/1/`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(content),
      });
      if (res.ok) { setContent(await res.json()); Swal.fire({ icon: 'success', title: 'Saved!', text: 'Returns page updated', timer: 1500, showConfirmButton: false }); }
      else throw new Error();
    } catch { Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to save' }); }
    finally { setSaving(false); }
  };

  if (loading) return (
    <div className="homepage-cms-container"><div className="cms-card"><div className="card-content">
      <div className="cms-skeleton" style={{ height: 40 }} />
      <div className="cms-skeleton" style={{ height: 40, marginTop: 12 }} />
    </div></div></div>
  );

  return (
    <div className="homepage-cms-container">
      <form onSubmit={handleSubmit}>

        {/* Hero */}
        <div className="cms-card">
          <div className="card-header"><div className="card-header-left"><div className="card-icon icon-blue">{'\uD83D\uDD04'}</div><h3>Hero Section</h3></div></div>
          <div className="card-content">
            <div className="cms-grid-2">
              <div className="cms-form-group"><label>Hero Title (English)</label><input className="form-input" name="hero_title" value={content.hero_title} onChange={handleChange} /></div>
              <div className="cms-form-group"><label>Hero Title (??)</label><input className="form-input" name="hero_title_bn" value={content.hero_title_bn} onChange={handleChange} /></div>
            </div>
            <div className="cms-grid-2" style={{ marginTop: 8 }}>
              <div className="cms-form-group"><label>Hero Subtitle (English)</label><input className="form-input" name="hero_subtitle" value={content.hero_subtitle} onChange={handleChange} /></div>
              <div className="cms-form-group"><label>Hero Subtitle (??)</label><input className="form-input" name="hero_subtitle_bn" value={content.hero_subtitle_bn} onChange={handleChange} /></div>
            </div>
          </div>
        </div>

        {/* Return Policy */}
        <div className="cms-card">
          <div className="card-header"><div className="card-header-left"><div className="card-icon icon-blue">{'\uD83D\uDCCB'}</div><h3>Return Policy</h3></div></div>
          <div className="card-content">
            <div className="cms-grid-2" style={{ marginBottom: 8 }}>
              <div className="cms-form-group"><label>Title (English)</label><input className="form-input" name="policy_title" value={content.policy_title} onChange={handleChange} /></div>
              <div className="cms-form-group"><label>Title (??)</label><input className="form-input" name="policy_title_bn" value={content.policy_title_bn} onChange={handleChange} /></div>
            </div>
            <div className="cms-grid-2" style={{ marginBottom: 8 }}>
              <div className="cms-form-group"><label>Content (English)</label><textarea className="form-input" name="policy_content" value={content.policy_content} onChange={handleChange} rows={4} style={{ resize: 'vertical' }} /></div>
              <div className="cms-form-group"><label>Content (??)</label><textarea className="form-input" name="policy_content_bn" value={content.policy_content_bn} onChange={handleChange} rows={4} style={{ resize: 'vertical' }} /></div>
            </div>
            <div className="cms-grid-2">
              <div className="cms-form-group"><label>Note (English)</label><input className="form-input" name="policy_note" value={content.policy_note} onChange={handleChange} /></div>
              <div className="cms-form-group"><label>Note (??)</label><input className="form-input" name="policy_note_bn" value={content.policy_note_bn} onChange={handleChange} /></div>
            </div>
          </div>
        </div>

        {/* Eligible Reasons */}
        <div className="cms-card">
          <div className="card-header">
            <div className="card-header-left"><div className="card-icon icon-blue">{'\u2705'}</div><h3>Eligible Return Reasons</h3></div>
            <button type="button" className="btn-add-feature" onClick={addReason}>+ Add Reason</button>
          </div>
          <div className="card-content">
            <div className="cms-grid-2" style={{ marginBottom: 12 }}>
              <div className="cms-form-group"><label>Section Title (English)</label><input className="form-input" name="eligible_title" value={content.eligible_title} onChange={handleChange} /></div>
              <div className="cms-form-group"><label>Section Title (??)</label><input className="form-input" name="eligible_title_bn" value={content.eligible_title_bn} onChange={handleChange} /></div>
            </div>
            {content.eligible_reasons.length === 0 && <div className="empty-state"><p>No reasons yet.</p></div>}
            {content.eligible_reasons.map((reason, i) => (
              <div key={i} className="feature-item-card" style={{ marginBottom: 8, padding: '8px 12px' }}>
                <div className="feature-header" style={{ marginBottom: 6 }}>
                  <span className="feature-count">#{i + 1}</span>
                  <button type="button" className="btn-remove-feature" onClick={() => removeReason(i)}>{'\u2715'}</button>
                </div>
                <div className="cms-grid-2">
                  <input className="form-input" value={reason} onChange={e => updateReason(i, e.target.value)} placeholder="Reason (English)" />
                  <input className="form-input" value={content.eligible_reasons_bn[i] || ''} onChange={e => updateReasonBn(i, e.target.value)} placeholder="Reason (??)" />
                </div>
              </div>
            ))}
            <div className="cms-grid-2" style={{ marginTop: 12 }}>
              <div className="cms-form-group"><label>Non-Eligible Note (English)</label><input className="form-input" name="non_eligible_note" value={content.non_eligible_note} onChange={handleChange} /></div>
              <div className="cms-form-group"><label>Non-Eligible Note (??)</label><input className="form-input" name="non_eligible_note_bn" value={content.non_eligible_note_bn} onChange={handleChange} /></div>
            </div>
          </div>
        </div>

        {/* Process Steps */}
        <div className="cms-card">
          <div className="card-header">
            <div className="card-header-left"><div className="card-icon icon-blue">{'\uD83D\uDCDD'}</div><h3>Return Process Steps</h3></div>
            <button type="button" className="btn-add-feature" onClick={addStep}>+ Add Step</button>
          </div>
          <div className="card-content">
            <div className="cms-grid-2" style={{ marginBottom: 12 }}>
              <div className="cms-form-group"><label>Section Title (English)</label><input className="form-input" name="process_title" value={content.process_title} onChange={handleChange} /></div>
              <div className="cms-form-group"><label>Section Title (??)</label><input className="form-input" name="process_title_bn" value={content.process_title_bn} onChange={handleChange} /></div>
            </div>
            {content.process_steps.length === 0 && <div className="empty-state"><p>No steps yet.</p></div>}
            {content.process_steps.map((step, i) => (
              <div key={i} className="feature-item-card" style={{ marginBottom: 10 }}>
                <div className="feature-header">
                  <span className="feature-count">Step {i + 1}</span>
                  <button type="button" className="btn-remove-feature" onClick={() => removeStep(i)}>{'\u2715'}</button>
                </div>
                <div className="cms-form-group" style={{ marginBottom: 8 }}>
                  <label>Icon (emoji)</label>
                  <input className="form-input" value={step.icon} onChange={e => updateStep(i, 'icon', e.target.value)} style={{ maxWidth: 120 }} />
                </div>
                <div className="cms-grid-2" style={{ marginBottom: 8 }}>
                  <div className="cms-form-group"><label>Title (English)</label><input className="form-input" value={step.title} onChange={e => updateStep(i, 'title', e.target.value)} /></div>
                  <div className="cms-form-group"><label>Title (??)</label><input className="form-input" value={step.title_bn} onChange={e => updateStep(i, 'title_bn', e.target.value)} /></div>
                </div>
                <div className="cms-grid-2">
                  <div className="cms-form-group"><label>Description (English)</label><textarea className="form-input" value={step.description} onChange={e => updateStep(i, 'description', e.target.value)} rows={2} style={{ resize: 'vertical' }} /></div>
                  <div className="cms-form-group"><label>Description (??)</label><textarea className="form-input" value={step.description_bn} onChange={e => updateStep(i, 'description_bn', e.target.value)} rows={2} style={{ resize: 'vertical' }} /></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Refund Info */}
        <div className="cms-card">
          <div className="card-header">
            <div className="card-header-left"><div className="card-icon icon-blue">{'\uD83D\uDCB0'}</div><h3>Refund Information</h3></div>
            <button type="button" className="btn-add-feature" onClick={addRefundRow}>+ Add Row</button>
          </div>
          <div className="card-content">
            <div className="cms-grid-2" style={{ marginBottom: 8 }}>
              <div className="cms-form-group"><label>Title (English)</label><input className="form-input" name="refund_title" value={content.refund_title} onChange={handleChange} /></div>
              <div className="cms-form-group"><label>Title (??)</label><input className="form-input" name="refund_title_bn" value={content.refund_title_bn} onChange={handleChange} /></div>
            </div>
            <div className="cms-grid-2" style={{ marginBottom: 12 }}>
              <div className="cms-form-group"><label>Content (English)</label><input className="form-input" name="refund_content" value={content.refund_content} onChange={handleChange} /></div>
              <div className="cms-form-group"><label>Content (??)</label><input className="form-input" name="refund_content_bn" value={content.refund_content_bn} onChange={handleChange} /></div>
            </div>
            {content.refund_table.map((row, i) => (
              <div key={i} className="feature-item-card" style={{ marginBottom: 8, padding: '8px 12px' }}>
                <div className="feature-header" style={{ marginBottom: 6 }}>
                  <span className="feature-count">#{i + 1}</span>
                  <button type="button" className="btn-remove-feature" onClick={() => removeRefundRow(i)}>{'\u2715'}</button>
                </div>
                <div className="cms-grid-2" style={{ marginBottom: 6 }}>
                  <input className="form-input" value={row.method} onChange={e => updateRefundRow(i, 'method', e.target.value)} placeholder="Payment Method (English)" />
                  <input className="form-input" value={row.method_bn} onChange={e => updateRefundRow(i, 'method_bn', e.target.value)} placeholder="Payment Method (??)" />
                </div>
                <div className="cms-grid-2">
                  <input className="form-input" value={row.time} onChange={e => updateRefundRow(i, 'time', e.target.value)} placeholder="Refund Time (English)" />
                  <input className="form-input" value={row.time_bn} onChange={e => updateRefundRow(i, 'time_bn', e.target.value)} placeholder="Refund Time (??)" />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Exchange */}
        <div className="cms-card">
          <div className="card-header"><div className="card-header-left"><div className="card-icon icon-blue">{'\uD83D\uDD01'}</div><h3>Exchange Policy</h3></div></div>
          <div className="card-content">
            <div className="cms-grid-2" style={{ marginBottom: 8 }}>
              <div className="cms-form-group"><label>Title (English)</label><input className="form-input" name="exchange_title" value={content.exchange_title} onChange={handleChange} /></div>
              <div className="cms-form-group"><label>Title (??)</label><input className="form-input" name="exchange_title_bn" value={content.exchange_title_bn} onChange={handleChange} /></div>
            </div>
            <div className="cms-grid-2">
              <div className="cms-form-group"><label>Content (English)</label><textarea className="form-input" name="exchange_content" value={content.exchange_content} onChange={handleChange} rows={4} style={{ resize: 'vertical' }} /></div>
              <div className="cms-form-group"><label>Content (??)</label><textarea className="form-input" name="exchange_content_bn" value={content.exchange_content_bn} onChange={handleChange} rows={4} style={{ resize: 'vertical' }} /></div>
            </div>
          </div>
        </div>

        <div className="cms-page-actions">
          <button type="submit" className="btn-save" disabled={saving}>{saving ? 'Saving...' : '\uD83D\uDCBE Save Changes'}</button>
        </div>
      </form>
    </div>
  );
};

export default ReturnsPageCMS;
